import { Button, Text, Title } from '@components/v2/ui';
import { MultiSelect } from '@components/v2/ui/MultiSelect/MultiSelect';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { PROJECT_TECHNOLOGIES } from '@config/constants/project-technologies';
import {
	ActionIcon,
	Drawer,
	Flex,
	Group,
	Radio,
	RangeSlider,
	Stack,
} from '@mantine/core';
import useStore from '@store/index';
import { IconX } from '@tabler/icons-react';
import { getCurrencyUnit } from '@utils/currency-units';
import {
	cloneDeep,
	convertArrayToLabelValue,
} from '@utils/functions';
import { getUnitLabel } from '@utils/units';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMeasurabilities } from 'shared/api/get-measurabilities';
import classes from './Filter.module.css';

export type RangeFilter = {
	$gte: number;
	$lte: number;
};

export type QueryParams = {
	entity?: string[];
	_id?: string[];
	technology?: string[];
	'location.countryCode'?: { $in: string[] };
	sellPrice_europertco2eq?: RangeFilter;
	'quantity.remaining_aco2'?: RangeFilter;
	active?: boolean;
	[key: string]: unknown;
};

type TFilterOffsetsProps = {
	opened: boolean;
	onClose: () => void;
	title: string;
	onApplyFilters: (filters: QueryParams) => void;
	value: QueryParams;
};

export const Filter = ({
	opened,
	onClose,
	title,
	onApplyFilters,
	value,
}: TFilterOffsetsProps) => {
	const { t } = useTranslation();
	const { data: projects } = useMeasurabilities();
	const {
		setOffsetsPage,
		setOffsetMesurabilityProjectsQueryParams,
	} = useStore();

	const customValuesStock = [
		0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 200, 300,
		400, 500, 600, 700, 800, 900, 1000, 2000, 3000, 4000,
		5000, 6000, 7000, 8000, 9000, 10000,
	];

	const [queryParams, setQueryParams] =
		useState<QueryParams>(value ?? {});
	const [valuePrice, setValuePrice] = useState<
		[number, number]
	>([0, 100]);
	const [stockIndices, setStockIndices] = useState<
		[number, number]
	>([0, customValuesStock.length - 1]);

	const findNearestIndex = (val: number) => {
		return customValuesStock.reduce(
			(prev, curr, idx) =>
				Math.abs(curr - val) <
				Math.abs(customValuesStock[prev] - val)
					? idx
					: prev,
			0
		);
	};

	const handleReset = () => {
		setOffsetMesurabilityProjectsQueryParams({});
		setValuePrice([0, 100]);
		setStockIndices([0, customValuesStock.length - 1]);
		setQueryParams({});
		onApplyFilters({});
		onClose();
	};

	const handleApplyFilters = () => {
		setOffsetMesurabilityProjectsQueryParams(queryParams);
		setOffsetsPage(1);
		onApplyFilters(queryParams);
		onClose();
	};

	useEffect(() => {
		if (opened) {
			setQueryParams(cloneDeep(value ?? {}));

			setValuePrice([
				value?.sellPrice_europertco2eq?.$gte ?? 0,
				value?.sellPrice_europertco2eq?.$lte ===
				1000000000000000
					? 100
					: (value?.sellPrice_europertco2eq?.$lte ?? 100),
			]);

			const gte =
				value?.['quantity.remaining_aco2']?.$gte ?? 0;
			const lteRaw =
				value?.['quantity.remaining_aco2']?.$lte;
			const lte =
				lteRaw === 10000000000 || !lteRaw
					? 10000
					: lteRaw;
			setStockIndices([
				findNearestIndex(gte),
				findNearestIndex(lte),
			]);
		}
	}, [opened, value]);

	return (
		<Drawer
			classNames={classes}
			opened={opened}
			position="right"
			onClose={onClose}
			withCloseButton={false}
			size="544px"
		>
			<Flex
				align="center"
				justify="space-between"
				gap="16px"
				pb="var(--mantine-spacing-xl)"
			>
				<Title
					order={6}
					c="var(--mantine-color-igluPrimary-9)"
				>
					{title}
				</Title>
				<ActionIcon
					color="var(--mantine-color-igluBorder-9)"
					radius="48px"
					variant="outline"
					size="48px"
					onClick={onClose}
				>
					<IconX size="24px" />
				</ActionIcon>
			</Flex>

			<Flex
				flex={1}
				direction="column"
				justify="space-between"
			>
				<Stack gap="var(--mantine-spacing-sm)">
					<Radio.Group
						label={t('global.status')}
						value={
							queryParams.active === true
								? 'verified'
								: queryParams.active === false
									? 'unverified'
									: 'all'
						}
						onChange={(val) => {
							if (val === 'all') {
								const { active, ...rest } = queryParams;
								setQueryParams(rest);
							} else {
								setQueryParams({
									...queryParams,
									active: val === 'verified',
								});
							}
						}}
					>
						<Group mt="xs">
							<Radio
								value="all"
								label={t('global.all')}
							/>
							<Radio
								value="verified"
								label={t('global.active')}
							/>
							<Radio
								value="unverified"
								label={t('global.inactive')}
							/>
						</Group>
					</Radio.Group>

					<MultiSelect
						placeholder={t(
							'filters.placeholders.selectProject'
						)}
						w="100%"
						comboboxProps={{ withinPortal: false }}
						data={convertArrayToLabelValue(projects)}
						clearable
						size="lg"
						searchable
						value={queryParams._id ?? []}
						onChange={(values) =>
							setQueryParams({
								...queryParams,
								_id: values,
							})
						}
					/>

					<MultiSelect
						placeholder={t(
							'filters.placeholders.selectTechnology'
						)}
						w="100%"
						comboboxProps={{ withinPortal: false }}
						data={PROJECT_TECHNOLOGIES}
						clearable
						size="lg"
						searchable
						value={queryParams.technology ?? []}
						onChange={(values) =>
							setQueryParams({
								...queryParams,
								technology: values,
							})
						}
					/>

					<MultiSelect
						placeholder={t(
							'filters.placeholders.selectCountry'
						)}
						w="100%"
						comboboxProps={{ withinPortal: false }}
						data={EUROPEAN_COUNTRIES}
						clearable
						size="lg"
						searchable
						value={
							queryParams['location.countryCode']?.$in ??
							[]
						}
						onChange={(values) => {
							if (values.length === 0) {
								const tmp = cloneDeep(queryParams);
								delete tmp['location.countryCode'];
								setQueryParams(tmp);
								return;
							}
							setQueryParams({
								...queryParams,
								'location.countryCode': { $in: values },
							});
						}}
					/>

					<Stack>
						<Text
							variant="button"
							c="var(--mantine-color-igluTextSecondary-9)"
						>
							{t('global.pricePerTonCO2')}
						</Text>
						<RangeSlider
							labelAlwaysOn
							color="var(--mantine-color-igluPrimary-9)"
							value={valuePrice}
							min={0}
							max={100}
							step={1}
							mb={'xs'}
							minRange={1}
							label={(v) =>
								v === 0 || v === 100
									? null
									: `${v} ${getCurrencyUnit('EUR')}`
							}
							marks={[
								{ value: 0, label: '0€' },
								{ value: 100, label: '100€' },
							]}
							onChange={(val) => {
								if (val[0] === 0 && val[1] === 100) {
									const tmp = cloneDeep(queryParams);
									delete tmp.sellPrice_europertco2eq;
									setQueryParams(tmp);
								} else {
									setQueryParams({
										...queryParams,
										sellPrice_europertco2eq: {
											$gte: val[0],
											$lte:
												val[1] === 100
													? 1000000000000000
													: val[1],
										},
									});
								}
								setValuePrice(val);
							}}
						/>
					</Stack>

					<Stack>
						<Text
							variant="button"
							c="var(--mantine-color-igluTextSecondary-9)"
						>
							{t('global.availableStock')}
						</Text>
						<RangeSlider
							labelAlwaysOn
							color="var(--mantine-color-igluPrimary-9)"
							value={stockIndices}
							min={0}
							max={customValuesStock.length - 1}
							step={1}
							minRange={1}
							label={(idx) =>
								idx === 0 ||
								idx === customValuesStock.length - 1
									? null
									: `${customValuesStock[idx]} ${getUnitLabel('tco2')}`
							}
							marks={[
								{ value: 0, label: '0' },
								{ value: 10, label: '100' },
								{ value: 19, label: '1k' },
								{ value: 28, label: '+10k' },
							]}
							onChange={(indices) => {
								const gte = customValuesStock[indices[0]];
								const lteVal =
									customValuesStock[indices[1]];
								if (
									indices[0] === 0 &&
									indices[1] ===
										customValuesStock.length - 1
								) {
									const tmp = cloneDeep(queryParams);
									delete tmp['quantity.remaining_aco2'];
									setQueryParams(tmp);
								} else {
									setQueryParams({
										...queryParams,
										'quantity.remaining_aco2': {
											$gte: gte,
											$lte:
												indices[1] ===
												customValuesStock.length - 1
													? 10000000000
													: lteVal,
										},
									});
								}
								setStockIndices(indices);
							}}
						/>
					</Stack>
				</Stack>

				<Flex
					direction={{
						base: 'column-reverse',
						sm: 'row',
					}}
					gap={{
						base: 'var(--mantine-spacing-sm)',
						sm: 'var(--mantine-spacing-lg)',
					}}
					mt="xl"
				>
					<Button
						fullWidth
						variant="outline"
						onClick={handleReset}
					>
						{t('global.resetFilters')}
					</Button>
					<Button
						fullWidth
						variant="primary"
						onClick={handleApplyFilters}
					>
						{t('global.applyFilters')}
					</Button>
				</Flex>
			</Flex>
		</Drawer>
	);
};
